import React, { useState, useEffect } from 'react';
import { X, Wallet, RefreshCw, ExternalLink, DollarSign, Calendar, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useBusiness } from '../contexts/BusinessContext';

interface PayoutDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  payout: any;
}

export default function PayoutDetailsModal({
  isOpen,
  onClose,
  payout
}: PayoutDetailsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [payoutDetails, setPayoutDetails] = useState<any>(null);
  const [linkedPayments, setLinkedPayments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && payout) {
      fetchPayoutDetails();
    }
  }, [isOpen, payout]);

  const fetchPayoutDetails = async () => {
    if (!payout?.id) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-payout-details', {
        body: { payoutId: payout.id }
      });

      if (error) throw error;
      
      setPayoutDetails(data?.payout);
      setLinkedPayments(data?.linkedPayments || []);
    } catch (error) {
      console.error('Error fetching payout details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'in_transit':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Clock className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'in_transit':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const openStripeDashboard = () => {
    if (selectedBusiness?.stripe_connect_account_id && payout?.stripe_payout_id) {
      const url = `https://dashboard.stripe.com/connect/accounts/${selectedBusiness.stripe_connect_account_id}/payouts/${payout.stripe_payout_id}`;
      window.open(url, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" 
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Payout Details</h2>
              <p className="text-sm text-gray-500">Stripe Payout Information</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Loading payout details...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Payout Summary */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 mb-6 border border-purple-200">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Payout Amount</p>
                    <p className="text-3xl font-bold text-gray-900">
                      ${(payout?.amount || payoutDetails?.amount || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${getStatusColor(payout?.status || payoutDetails?.status)}`}>
                    {getStatusIcon(payout?.status || payoutDetails?.status)}
                    <span className="font-semibold text-sm">
                      {formatStatus(payout?.status || payoutDetails?.status)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <p className="text-xs text-gray-600 mb-1">Payout ID</p>
                    <p className="text-sm font-mono text-gray-900">
                      {payout?.stripe_payout_id || payoutDetails?.stripe_payout_id || 'N/A'}
                    </p>
                  </div>
                  {payout?.arrival_date || payoutDetails?.arrival_date ? (
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Arrival Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {new Date(payout?.arrival_date || payoutDetails?.arrival_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  ) : null}
                </div>

                {payout?.payout_method || payoutDetails?.payout_method ? (
                  <div className="mt-4 pt-4 border-t border-purple-200">
                    <p className="text-xs text-gray-600 mb-1">Transfer Method</p>
                    <p className="text-sm font-medium text-gray-900">
                      {(payout?.payout_method || payoutDetails?.payout_method) === 'instant' ? 'Instant Transfer' : 'Standard Transfer'}
                    </p>
                  </div>
                ) : null}
              </div>

              {/* Linked Payments */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Linked Payments</h3>
                  <span className="text-sm text-gray-500">
                    {linkedPayments.length} {linkedPayments.length === 1 ? 'payment' : 'payments'}
                  </span>
                </div>

                {linkedPayments.length === 0 ? (
                  <div className="bg-gray-50 rounded-lg p-6 text-center border border-gray-200">
                    <DollarSign className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-600">No payments linked to this payout yet</p>
                    <p className="text-xs text-gray-500 mt-1">Payments will be linked automatically when synced</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {linkedPayments.map((item: any) => (
                      <div
                        key={item.mappingId}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {item.payment?.customer ? (
                              <div className="mb-2">
                                <p className="text-sm font-semibold text-gray-900">
                                  {item.payment.customer.full_name || 
                                   `${item.payment.customer.first_name} ${item.payment.customer.last_name}`}
                                </p>
                                {item.payment.bookingNumber && (
                                  <p className="text-xs text-gray-500">Booking #{item.payment.bookingNumber}</p>
                                )}
                                {item.payment.saleNumber && (
                                  <p className="text-xs text-gray-500">Sale #{item.payment.saleNumber}</p>
                                )}
                              </div>
                            ) : (
                              <p className="text-sm font-semibold text-gray-900 mb-2">Payment</p>
                            )}
                            <div className="flex items-center space-x-4 text-xs text-gray-600">
                              <span>Amount: <span className="font-semibold text-gray-900">${item.amount.toLocaleString()}</span></span>
                              {item.payment?.createdAt && (
                                <span>
                                  {new Date(item.payment.createdAt).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                              item.payment?.status === 'completed' 
                                ? 'bg-green-50 text-green-700' 
                                : 'bg-yellow-50 text-yellow-700'
                            }`}>
                              {item.payment?.status || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                <button
                  onClick={fetchPayoutDetails}
                  className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Refresh</span>
                </button>
                {selectedBusiness?.stripe_connect_account_id && payout?.stripe_payout_id && (
                  <button
                    onClick={openStripeDashboard}
                    className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>View in Stripe</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

